import { Icon } from '@dixa/lib-shared-habla';
import { SearchUser } from 'lib-search-types';
import { ComponentProps, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';

import {
  HookFormSearchSelect,
  HookFormSearchSelectResult,
} from 'components/hookForm';

import { FormValues } from '../composeEmailModal.types';

import { ContactItemContent } from './contactItemContent';

type ComponontPropsType = Omit<
  ComponentProps<typeof HookFormSearchSelect>['componentProps'],
  'getItemTitleByValue'
>;

type ComposeEmailFieldEmailContactProps = {
  name: 'toEmail' | 'cc' | 'bcc';
  label: string;
  multiple?: boolean;
  required?: boolean;
  componentProps?: ComponontPropsType;
};

const createSearchUser = (input: string): SearchUser => ({
  id: 'suggestion',
  name: input,
  email: input,
  type: 'contact',
  org_id: null,
  phone_number: null,
  avatar_url: null,
  company_id: null,
  default_outbound_number: null,
  anonymized_at: null,
  given_name: null,
  family_name: null,
  has_scim_enabled: false,
  external_id: null,
});

const isSearchUser = (
  result: HookFormSearchSelectResult,
): result is SearchUser => {
  if (result && typeof result === 'object' && 'email' in result) {
    return true;
  }
  return false;
};

export const ComposeEmailFieldEmailContact = ({
  name,
  label,
  multiple = false,
  required = false,
  componentProps,
}: ComposeEmailFieldEmailContactProps): JSX.Element => {
  const { control, getValues, setValue } = useFormContext<FormValues>();
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const commitName = useCallback(() => {
    if (!editingEmail) return;
    setValue(
      name,
      getValues(name).map((e) =>
        e.email === editingEmail ? { ...e, name: editingName || e.email } : e,
      ),
    );
    setEditingEmail(null);
  }, [editingEmail, editingName, getValues, name, setValue]);

  const getSuggestions = useCallback(
    (searchTerm: string, results: readonly SearchUser[]) => {
      if (isEmail(searchTerm)) {
        const suggestedUser = createSearchUser(searchTerm);

        const existsInResults = results.some((result) => {
          const hitKeys = Object.keys(result);
          // if any property of the hit matches, we consider it to exist so we don't show a duplicate hit
          for (const key of hitKeys) {
            if (result[key] === searchTerm) {
              return true;
            }
          }
        });

        if (!existsInResults) {
          return [suggestedUser];
        }
      }

      return [];
    },
    [],
  );

  return (
    <HookFormSearchSelect
      control={control}
      name={name}
      label={label}
      rules={
        required
          ? {
              required: 'Please select an email address',
            }
          : undefined
      }
      componentProps={{
        ...componentProps,
        multiple,
        clearable: true,
        placeholder: 'Select or type an email address',
        equalityFn: (a, b) => a?.email === b?.email,
        getItemTitleByValue: (value) => {
          if (!value) return '';

          if (value.id === 'suggestion' && editingEmail === value.email) {
            return (
              <input
                autoFocus
                type="text"
                value={editingName}
                placeholder="Add a name..."
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitName();
                  }
                  if (e.key === 'Escape') setEditingEmail(null);
                  e.stopPropagation();
                }}
                onBlur={commitName}
                onClick={(e) => e.stopPropagation()}
                style={{ all: 'unset', width: '120px' }}
              />
            );
          }

          if (value.id === 'suggestion') {
            const label =
              value.name && value.name !== value.email ? (
                <>
                  <strong>{value.name}</strong>
                  {` <${value.email}>`}
                </>
              ) : (
                value.email
              );
            return (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingName(
                      value.name && value.name !== value.email
                        ? value.name
                        : '',
                    );
                    setEditingEmail(value.email);
                  }}
                  style={{ all: 'unset', cursor: 'pointer', lineHeight: 0 }}
                  title="Add a display name"
                >
                  <Icon name="pen" size={12} />
                </button>
              </span>
            );
          }

          return value.name && value.email ? (
            <span>
              <strong>{value.name}</strong>
              {` <${value.email}>`}
            </span>
          ) : (
            (value.email ?? value.name ?? '')
          );
        },
      }}
      scope="users"
      initialArg={{
        sortBy: { field: 'display_name', order: 'asc' },
        pageSize: 20,
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [{ value: 'member' }, { value: 'contact' }],
            },
            {
              field: 'email',
              operator: 'is_not_empty',
              values: [],
            },
          ],
        },
      }}
      getSuggestions={getSuggestions}
      transformValue={(user) => ({
        email: user.email || '',
        name: user.id === 'suggestion' ? '' : user.name || user.email || '',
        id: user.id || undefined,
      })}
      renderSelectionListItemContent={(user) => {
        if (!isSearchUser(user)) {
          return null;
        }

        return (
          <ContactItemContent
            id={user.id}
            name={user.name || ''}
            email={user.email || ''}
            avatarUrl={user.avatar_url ?? undefined}
          />
        );
      }}
    />
  );
};
